"use client";

/**
 * Shared Animated Components
 * Reusable animation wrappers using Framer Motion
 * These encapsulate AnimatePresence + motion patterns for consistent usage
 */

import * as React from "react";
import { motion, AnimatePresence, type HTMLMotionProps } from "framer-motion";
import {
  collapsibleVariants,
  collapsibleFastVariants,
  fadeVariants,
  fadeScaleVariants,
  fadeScaleSmallVariants,
  slideLeftVariants,
  slideRightVariants,
  slideUpVariants,
  slideDownVariants,
  staggerContainerVariants,
  staggerContainerFastVariants,
  staggerItemVariants,
  staggerItemCustomVariants,
  overlayVariants,
  modalVariants,
  rotateChevronTransition,
  DURATION,
} from "@/constants";
import { cn } from "@/shared/core/utils";

// ============================================================================
// TYPES
// ============================================================================

interface AnimatedPresenceProps {
  /** Whether the children should be visible */
  show: boolean;
  /** Content to animate */
  children: React.ReactNode;
  /** Additional className for the motion wrapper */
  className?: string;
  /** AnimatePresence mode */
  mode?: "sync" | "wait" | "popLayout";
  /** Key for AnimatePresence (useful for list transitions) */
  presenceKey?: string;
}

interface AnimatedContainerProps
  extends Omit<HTMLMotionProps<"div">, "initial" | "animate" | "exit"> {
  children: React.ReactNode;
  className?: string;
}

interface StaggerItemProps extends AnimatedContainerProps {
  /** Index for staggered delay calculation */
  index?: number;
}

// ============================================================================
// ANIMATED PRESENCE WRAPPERS
// ============================================================================

/**
 * Animated container for collapsible/expandable content
 * Handles height animation with fade
 */
export function AnimatedCollapsible({
  show,
  children,
  className,
  mode = "wait",
  presenceKey,
}: AnimatedPresenceProps) {
  return (
    <AnimatePresence mode={mode}>
      {show && (
        <motion.div
          key={presenceKey}
          variants={collapsibleVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className={cn("overflow-hidden", className)}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Fast collapsible for smaller UI elements
 */
export function AnimatedCollapsibleFast({
  show,
  children,
  className,
  mode = "wait",
  presenceKey,
}: AnimatedPresenceProps) {
  return (
    <AnimatePresence mode={mode}>
      {show && (
        <motion.div
          key={presenceKey}
          variants={collapsibleFastVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className={cn("overflow-hidden", className)}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Simple fade in/out animation
 */
export function AnimatedFade({
  show,
  children,
  className,
  mode = "wait",
  presenceKey,
}: AnimatedPresenceProps) {
  return (
    <AnimatePresence mode={mode}>
      {show && (
        <motion.div
          key={presenceKey}
          variants={fadeVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={className}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Fade with scale animation (good for modals, cards)
 */
export function AnimatedFadeScale({
  show,
  children,
  className,
  mode = "wait",
  presenceKey,
}: AnimatedPresenceProps) {
  return (
    <AnimatePresence mode={mode}>
      {show && (
        <motion.div
          key={presenceKey}
          variants={fadeScaleVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={className}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Small fade + scale (good for buttons, icons)
 */
export function AnimatedFadeScaleSmall({
  show,
  children,
  className,
  mode = "wait",
  presenceKey,
}: AnimatedPresenceProps) {
  return (
    <AnimatePresence mode={mode}>
      {show && (
        <motion.div
          key={presenceKey}
          variants={fadeScaleSmallVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={className}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Slide from left animation
 */
export function AnimatedSlideLeft({
  show,
  children,
  className,
  mode = "wait",
  presenceKey,
}: AnimatedPresenceProps) {
  return (
    <AnimatePresence mode={mode}>
      {show && (
        <motion.div
          key={presenceKey}
          variants={slideLeftVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={className}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Slide from right animation
 */
export function AnimatedSlideRight({
  show,
  children,
  className,
  mode = "wait",
  presenceKey,
}: AnimatedPresenceProps) {
  return (
    <AnimatePresence mode={mode}>
      {show && (
        <motion.div
          key={presenceKey}
          variants={slideRightVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={className}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Slide from top animation
 */
export function AnimatedSlideUp({
  show,
  children,
  className,
  mode = "wait",
  presenceKey,
}: AnimatedPresenceProps) {
  return (
    <AnimatePresence mode={mode}>
      {show && (
        <motion.div
          key={presenceKey}
          variants={slideUpVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={className}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Slide from bottom animation
 */
export function AnimatedSlideDown({
  show,
  children,
  className,
  mode = "wait",
  presenceKey,
}: AnimatedPresenceProps) {
  return (
    <AnimatePresence mode={mode}>
      {show && (
        <motion.div
          key={presenceKey}
          variants={slideDownVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={className}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// MODAL / OVERLAY COMPONENTS
// ============================================================================

/**
 * Modal backdrop/overlay with fade animation
 */
export function AnimatedOverlay({
  show,
  children,
  className,
  onClick,
}: AnimatedPresenceProps & { onClick?: (e: React.MouseEvent<HTMLDivElement>) => void }) {
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Only trigger onClick if clicking directly on the backdrop (not on children)
      if (e.target === e.currentTarget && onClick) {
        onClick(e);
      }
    },
    [onClick]
  );

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={cn(
            "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm",
            className
          )}
          onClick={handleClick}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Modal content with scale + fade animation
 */
export function AnimatedModal({
  show,
  children,
  className,
  mode = "wait",
}: AnimatedPresenceProps) {
  return (
    <AnimatePresence mode={mode}>
      {show && (
        <motion.div
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={className}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// STAGGER COMPONENTS (for lists)
// ============================================================================

/**
 * Container for staggered children animations
 * Wrap your list items with StaggerItem
 */
export function StaggerContainer({
  children,
  className,
  ...props
}: AnimatedContainerProps) {
  return (
    <motion.div
      variants={staggerContainerVariants}
      initial="hidden"
      animate="visible"
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/**
 * Fast stagger container (shorter delays)
 */
export function StaggerContainerFast({
  children,
  className,
  ...props
}: AnimatedContainerProps) {
  return (
    <motion.div
      variants={staggerContainerFastVariants}
      initial="hidden"
      animate="visible"
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/**
 * Stagger item - use inside StaggerContainer
 */
export function StaggerItem({
  children,
  className,
  ...props
}: AnimatedContainerProps) {
  return (
    <motion.div variants={staggerItemVariants} className={className} {...props}>
      {children}
    </motion.div>
  );
}

/**
 * Stagger item with custom index-based delay
 * Use when you need fine control over stagger timing
 */
export function StaggerItemCustom({
  children,
  className,
  index = 0,
  ...props
}: StaggerItemProps) {
  return (
    <motion.div
      custom={index}
      variants={staggerItemCustomVariants}
      initial="hidden"
      animate="visible"
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// ============================================================================
// UTILITY COMPONENTS
// ============================================================================

/**
 * Animated chevron that rotates 180° when open
 */
export function AnimatedChevron({
  isOpen,
  children,
  className,
}: {
  isOpen: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      animate={{ rotate: isOpen ? 180 : 0 }}
      transition={rotateChevronTransition}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Animated chevron that rotates 90° when open
 */
export function AnimatedChevron90({
  isOpen,
  children,
  className,
}: {
  isOpen: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      animate={{ rotate: isOpen ? 90 : 0 }}
      transition={rotateChevronTransition}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ============================================================================
// GENERIC ANIMATED WRAPPER
// ============================================================================

type AnimationVariant =
  | "fade"
  | "fadeScale"
  | "fadeScaleSmall"
  | "collapsible"
  | "collapsibleFast"
  | "slideLeft"
  | "slideRight"
  | "slideUp"
  | "slideDown"
  | "modal"
  | "overlay";

const variantMap = {
  fade: fadeVariants,
  fadeScale: fadeScaleVariants,
  fadeScaleSmall: fadeScaleSmallVariants,
  collapsible: collapsibleVariants,
  collapsibleFast: collapsibleFastVariants,
  slideLeft: slideLeftVariants,
  slideRight: slideRightVariants,
  slideUp: slideUpVariants,
  slideDown: slideDownVariants,
  modal: modalVariants,
  overlay: overlayVariants,
};

/**
 * Generic animated presence wrapper
 * Use this when you want to pick animation type dynamically
 */
export function Animated({
  show,
  variant = "fade",
  children,
  className,
  mode = "wait",
  presenceKey,
}: AnimatedPresenceProps & { variant?: AnimationVariant }) {
  const variants = variantMap[variant];
  const exitKey =
    variant === "collapsible" || variant === "collapsibleFast"
      ? "hidden"
      : "exit";

  return (
    <AnimatePresence mode={mode}>
      {show && (
        <motion.div
          key={presenceKey}
          variants={variants}
          initial="hidden"
          animate="visible"
          exit={exitKey}
          className={cn(
            (variant === "collapsible" || variant === "collapsibleFast") &&
              "overflow-hidden",
            className
          )}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// INTERACTIVE COMPONENTS (hover/tap animations)
// ============================================================================

interface InteractiveProps {
  children: React.ReactNode;
  className?: string;
  /** Whether interactions are disabled */
  disabled?: boolean;
  /** Custom hover scale (default: 1.02) */
  hoverScale?: number;
  /** Custom tap scale (default: 0.98) */
  tapScale?: number;
  /** Click handler */
  onClick?: () => void;
}

/**
 * Interactive wrapper with hover/tap scale animations
 * Use for buttons, cards, list items that should react to interactions
 */
export function AnimatedInteractive({
  children,
  className,
  disabled = false,
  hoverScale = 1.02,
  tapScale = 0.98,
  onClick,
}: InteractiveProps) {
  return (
    <motion.div
      whileHover={disabled ? undefined : { scale: hoverScale }}
      whileTap={disabled ? undefined : { scale: tapScale }}
      className={className}
      onClick={disabled ? undefined : onClick}
    >
      {children}
    </motion.div>
  );
}

/**
 * Interactive button with hover/tap animations
 * Renders as a button element
 */
export function AnimatedButton({
  children,
  className,
  disabled = false,
  hoverScale = 1.02,
  tapScale = 0.98,
  onClick,
  type = "button",
  title,
}: InteractiveProps & {
  type?: "button" | "submit" | "reset";
  title?: string;
}) {
  return (
    <motion.button
      type={type}
      whileHover={disabled ? undefined : { scale: hoverScale }}
      whileTap={disabled ? undefined : { scale: tapScale }}
      className={className}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </motion.button>
  );
}

// ============================================================================
// RE-EXPORT motion and AnimatePresence for advanced use cases
// ============================================================================

/**
 * Re-export motion for advanced cases where shared components don't fit.
 * Prefer using the shared animated components when possible.
 */
export { motion, AnimatePresence } from "framer-motion";
